export type ApiConversation = {
  id: string;
  client_user_id: string;
  status: 'OPEN' | 'CLOSED';
  mode: 'AI' | 'HUMAN' | 'HYBRID';
  city_code: string;
  assigned_admin_id: string | null;
  last_message_at: string | null;
  returned_to_ai_at: string | null;
  created_at: string;
  updated_at: string;
};

export type ApiMessage = {
  id: string;
  conversation_id: string;
  sender_type: 'USER' | 'OPERATOR' | 'AI' | 'SYSTEM';
  sender_user_id: string | null;
  text: string;
  meta: any;
  created_at: string;
  attachments?: ApiAttachment[];
};

export type ApiAttachment = {
  id: string;
  conversation_id: string;
  message_id: string | null;
  uploaded_by_user_id: string;
  bucket: string;
  path: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
  url?: string;
  expiresIn?: number;
};

export type ListMessagesResponse = {
  items: ApiMessage[];
  nextCursor: string | null;
};

export type HandoffSuggestedProvider = {
  id: string;
  name: string;
  /** Pre-selected in the create-order draft. Set only for a provider the client asked for again. */
  is_primary: boolean;
  /** Whose turn it is under plain rotation. Independent of is_primary. */
  is_recommended: boolean;
  /** This provider served the client before and the client asked for them again. */
  is_previous_for_client: boolean;
  /** Lifetime count of orders this provider completed. */
  completed_orders_count: number;
};

/**
 * The provider the client asked for again. `eligible: false` means they are no
 * longer assignable for this case, so they are not in suggested_providers.
 */
export type HandoffPreviousProvider = {
  id: string;
  name: string;
  eligible: boolean;
};

export type HandoffRow = {
  id: string;
  conversation_id: string;
  city_code: string;
  status: 'PENDING' | 'TAKEN' | 'CLOSED';
  category: string | null;
  reason: string | null;
  ai_summary: any;
  suggested_category_id: string | null;
  suggested_providers: HandoffSuggestedProvider[];
  previous_provider: HandoffPreviousProvider | null;
  created_at: string;
  taken_by_admin_id: string | null;
  taken_at: string | null;
  customer_id: string | null;
  customer_preferred_name: string | null;
  customer_phone: string | null;
  customer_address: string | null;
};

export type HandoffInboxResponse = {
  items: HandoffRow[];
  page: number;
  pageSize: number;
  total: number;
};

// ─── Orders ────────────────────────────────────────────────────────────────────

export type ServiceModel = 'referral_dispatch' | 'managed_dispatch';

export type OrderStatus =
  | 'waiting_for_customer'
  | 'finding_provider'
  | 'waiting_for_payment'
  | 'confirmed'
  | 'in_progress'
  | 'completed'
  | 'referral_sent'
  | 'completed_externally'
  | 'closed'
  | 'cancelled'
  | 'under_review';

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  waiting_for_customer: 'Waiting for Customer',
  finding_provider: 'Finding Provider',
  waiting_for_payment: 'Waiting for Payment',
  confirmed: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  referral_sent: 'Referral Sent',
  completed_externally: 'Completed Externally',
  closed: 'Closed',
  cancelled: 'Cancelled',
  under_review: 'Under Review',
};

// Statuses with no entry here are valid for both service models.
export const STATUS_SERVICE_MODEL: Partial<Record<OrderStatus, ServiceModel>> = {
  confirmed:            'managed_dispatch',
  in_progress:          'managed_dispatch',
  completed:            'managed_dispatch',
  referral_sent:        'referral_dispatch',
  completed_externally: 'referral_dispatch',
};

/**
 * Allowed status transitions. Mirrors `ALLOWED_STATUS_TRANSITIONS` in the API
 * (`apps/api/src/orders/dto/update-order-status.dto.ts`) — the backend is the
 * authoritative copy and rejects illegal transitions regardless of what the
 * panel shows. This copy exists purely as UX sugar so an operator sees which
 * moves are possible instead of discovering it through a 400.
 *
 * Keep in sync with the backend map when the state machine changes.
 */
export const ALLOWED_STATUS_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  waiting_for_customer: ['finding_provider', 'waiting_for_payment', 'confirmed', 'referral_sent', 'under_review', 'cancelled', 'closed'],
  finding_provider:     ['waiting_for_customer', 'waiting_for_payment', 'confirmed', 'referral_sent', 'under_review', 'cancelled', 'closed'],
  waiting_for_payment:  ['waiting_for_customer', 'finding_provider', 'confirmed', 'referral_sent', 'under_review', 'cancelled', 'closed'],
  confirmed:            ['in_progress', 'completed', 'waiting_for_payment', 'finding_provider', 'under_review', 'cancelled', 'closed'],
  in_progress:          ['completed', 'under_review', 'cancelled', 'closed'],
  referral_sent:        ['completed_externally', 'under_review', 'cancelled', 'closed'],
  under_review:         ['waiting_for_customer', 'finding_provider', 'waiting_for_payment', 'confirmed', 'in_progress', 'referral_sent', 'completed', 'completed_externally', 'cancelled', 'closed'],
  completed:            ['under_review'],
  completed_externally: ['under_review'],
  closed:               ['under_review'],
  cancelled:            ['under_review'],
};

export const SERVICE_MODEL_LABELS: Record<ServiceModel, string> = {
  referral_dispatch: 'Referral Dispatch',
  managed_dispatch: 'Managed Dispatch',
};

export type PaymentType = 'deposit' | 'full' | 'installment';

export const PAYMENT_TYPE_LABELS: Record<PaymentType, string> = {
  deposit: 'Deposit',
  full: 'Full payment',
  installment: 'Installment',
};

export type PaymentStatus =
  | 'not_required'
  | 'pending'
  | 'awaiting'
  | 'paid'
  | 'refunded'
  | 'failed';

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  not_required: 'Not required',
  pending: 'Pending',
  awaiting: 'Awaiting payment',
  paid: 'Paid',
  refunded: 'Refunded',
  failed: 'Failed',
};

export type ApiCity = {
  code: string;
  label: string;
};

export type ApiCategory = {
  category_id: string;
  name: string;
  vertical: string;
  description_ai: string;
};

export type ApiAdminCategory = {
  category_id: string;
  name: string;
  vertical: string;
  description_ai: string;
  example_requests: string[] | null;
  is_active: boolean;
  created_at: string;
  city_availability: { city_code: string; is_active: boolean }[];
  provider_count: number;
};

export type CreateCategoryPayload = {
  name: string;
  vertical: string;
  description_ai: string;
  example_requests?: string[];
  confirmation_questions?: string[];
  is_active?: boolean;
  service_models?: string[];
  category_id?: string;
};

export type ProviderAssignmentType = 'assigned' | 'other_in_notes' | 'unknown' | 'unassigned';

export type ApiProvider = {
  id: string;
  name: string;
  contact_name: string | null;
  phone: string | null;
  secondary_phone: string | null;
  email: string | null;
  city_codes: string[];
  category_ids: string[];
  preferred_contact_method: string;
  internal_notes: string | null;
  admin_notes: string | null;
  languages: string[];
  insurance_verified: boolean;
  wcb_registered: boolean;
  verified_at: string | null;
  working_hours: Record<string, { open: boolean; from?: string; to?: string }> | null;
  trust_status: 'trusted' | 'caution' | 'pre_block' | 'blocked';
  is_active: boolean;
  created_at: string;
  last_assigned_at: string | null;
  assigned_orders_count: number;
};

export type ApiOrder = {
  id: string;
  order_code: string;
  conversation_id: string;
  handoff_request_id: string | null;
  created_by_admin_id: string;
  city_code: string;
  service_model: ServiceModel;
  status: OrderStatus;
  category: string | null;
  service_name: string | null;
  scheduled_at: string | null;
  scheduled_timezone: string | null;
  address: string | null;
  address_access_notes: string | null;
  internal_notes: string | null;
  provider_assignment_type: ProviderAssignmentType;
  provider_id: string | null;
  provider_notes: string | null;
  providers: ApiProvider | null;   // Supabase join alias
  payment_provider: string | null;
  payment_status: PaymentStatus | null;
  payment_type: PaymentType | null;
  payment_amount_cents: number | null;
  payment_currency: string | null;
  stripe_payment_link_id: string | null;
  stripe_payment_link_url: string | null;
  stripe_customer_id: string | null;
  payment_requested_at: string | null;
  payment_paid_at: string | null;
  payment_last_synced_at: string | null;
  quote_amount_cents: number | null;
  estimated_duration_minutes: number | null;
  completion_notes: string | null;
  cancelled_reason: string | null;
  assigned_at: string | null;
  confirmed_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
};

export type SearchOrderRow = ApiOrder & {
  handoff: {
    customer_preferred_name: string | null;
    customer_phone: string | null;
  } | null;
};

export type ApiOrderDocument = {
  id: string;
  order_id: string;
  document_kind: string;
  original_filename: string;
  bucket: string;
  path: string;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
};

export type ApiOrderStatusHistoryEntry = {
  id: string;
  order_id: string;
  changed_by_admin_id: string | null;
  /** Email (or other resolved label) of the operator who made the change. Null = automated. */
  changed_by_display_name: string | null;
  from_status: OrderStatus | null;
  to_status: OrderStatus;
  reason: string | null;
  meta: Record<string, unknown>;
  created_at: string;
};

export type ApiAppUser = {
  id: string;
  email: string | null;
  role: 'USER';
  preferred_name: string;
  city_code: string;
  phone: string | null;
  address: string | null;
  joined_at: string;
};

// ── SUPERADMIN statistics (GET /admin/stats) ───────────────────────────────

export type StatsDays = 7 | 30 | 90;

export interface ApiStatsDayCount {
  day: string; // 'YYYY-MM-DD'
  count: number;
}

export interface ApiAdminStats {
  days: StatsDays;
  waitlist: {
    total: number;
    inPeriod: number;
    byDay: ApiStatsDayCount[];
    byCity: Array<{ city: string; count: number }>;
    byService: Array<{ service: string; count: number }>;
    byUtmSource: Array<{ source: string; count: number }>;
    byUtmCampaign: Array<{ campaign: string; count: number }>;
  };
  providerApplications: {
    total: number;
    inPeriod: number;
    byDay: ApiStatsDayCount[];
  };
  goldenKey: {
    attemptsInPeriod: number;
    purchasesInPeriod: number;
    purchasesTotal: number;
    revenueCentsInPeriod: number;
    revenueCentsTotal: number;
    blockedInPeriod: Array<{ reason: string; count: number }>;
  };
  contactMessages: { total: number; inPeriod: number };
  consent: {
    inPeriod: { acceptedAll: number; partial: number; rejectedAll: number };
  };
  orders: {
    pipeline: Array<{ status: string; count: number }>;
    createdByDay: ApiStatsDayCount[];
    completedByDay: ApiStatsDayCount[];
    closedByDay: ApiStatsDayCount[];
    createdInPeriod: number;
    completedInPeriod: number;
    cancelledInPeriod: number;
    closedInPeriod: number;
    paidRevenueCentsInPeriod: number;
  };
}
